import type { RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
  // ===========================================
  // PUBLIC ROUTES (No authentication required)
  // ===========================================
  {
    path: '/',
    component: () => import('src/views/layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('src/views/pages/public/LoginPage.vue'),
        meta: { titleKey: 'common.auth.login.title', skipLink: false },
      },
      {
        path: 'unauthorized',
        name: 'unauthorized',
        component: () => import('src/views/pages/public/UnauthorizedPage.vue'),
        meta: { titleKey: 'common.auth.unauthorized.title' },
      },
    ],
  },

  // The login page lives at `/` (above), but router/index.ts's navigation
  // guards historically redirect unauthenticated requests to `/login`
  // (lines 35, 41, 79 — preserved here for compatibility) — and external
  // bookmarks / browser autofill / hardcoded share links commonly point
  // at `/login` too. Without this redirect, every one of those navigations
  // 404s. Mapping `/login` → `{ name: 'login' }` keeps the canonical URL
  // at `/` while making `/login` a permanent valid entry point. Using a
  // name-based redirect (not `redirect: '/'`) survives future path moves
  // of the login route.
  {
    path: '/login',
    redirect: { name: 'login' },
  },

  // ===========================================
  // AUTHENTICATED ROUTES (Staff access required)
  // ===========================================
  // All authenticated routes require: requiresAuth + requiresStaff
  // Additional permission gating uses a single `permission` (string) meta key —
  // the guard reads `to.meta.permission` only. A `permissions` (string[]) form
  // is NOT implemented; using it would silently leave the route ungated.

  // Dashboard - requires staff.dashboard.view
  {
    path: '/dashboard',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('src/views/pages/authenticated/DashboardPage.vue'),
        meta: {
          titleKey: 'staff.pages.dashboard.title',
          icon: 'mdi-view-dashboard',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.dashboard.view',
        },
      },
    ],
  },

  // Notifications — staff personal notification feed.
  // No `permission` meta because notifications are per-user; the policy
  // gates filter to the requesting user's own rows on every endpoint.
  {
    path: '/notifications',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'notifications',
        component: () => import('src/views/pages/authenticated/NotificationsIndexPage.vue'),
        meta: {
          titleKey: 'notifications.title',
          icon: 'mdi-bell',
          requiresAuth: true,
          requiresStaff: true,
        },
      },
      {
        path: 'settings',
        name: 'notification-settings',
        component: () => import('src/views/pages/authenticated/NotificationSettingsPage.vue'),
        meta: {
          titleKey: 'notifications.settings.title',
          icon: 'mdi-bell-cog',
          requiresAuth: true,
          requiresStaff: true,
        },
      },
    ],
  },

  // Support Tickets - requires staff.tickets.view
  {
    path: '/tickets',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'tickets',
        component: () => import('src/views/pages/authenticated/TicketsPage.vue'),
        meta: {
          titleKey: 'staff.pages.tickets.title',
          icon: 'mdi-ticket',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.tickets.view',
        },
      },
    ],
  },

  // Support Chat Queue - requires staff.tickets.view
  {
    path: '/support-chat',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'support-chat',
        component: () => import('src/views/pages/authenticated/SupportChatPage.vue'),
        meta: {
          titleKey: 'staff.pages.supportChat.title',
          icon: 'chat',
          requiresAuth: true,
          requiresStaff: true,
          // Must match the API: every endpoint the page calls goes through
          // ChatPolicy (staff.chat.view/manage), NOT the tickets slugs — with
          // the tickets gate, chat-only staff never saw the page and
          // tickets-only staff got a page where every request 403s.
          permission: 'staff.chat.view',
        },
      },
      // KB-management pages moved to the /knowledge-management route group
      // (E-1, 20260621_085309). Old paths redirect so deep links / bookmarks
      // keep working; not duplicated.
      { path: 'knowledge', redirect: { name: 'knowledge-management' } },
      { path: 'review', redirect: { name: 'knowledge-management-review' } },
      { path: 'drafts', redirect: { name: 'knowledge-management-drafts' } },
      { path: 'content-health', redirect: { name: 'knowledge-management-health' } },
    ],
  },
  {
    path: '/knowledge-management',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'knowledge-management',
        component: () => import('src/views/pages/authenticated/KnowledgeBasePage.vue'),
        meta: {
          titleKey: 'staff.pages.knowledgeBase.title',
          icon: 'menu_book',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.chat.manage',
        },
      },
      {
        path: 'review',
        name: 'knowledge-management-review',
        component: () => import('src/views/pages/authenticated/ReviewQueuePage.vue'),
        meta: {
          titleKey: 'staff.pages.reviewQueue.title',
          icon: 'rate_review',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.chat.manage',
        },
      },
      {
        path: 'drafts',
        name: 'knowledge-management-drafts',
        component: () => import('src/views/pages/authenticated/DraftReviewPage.vue'),
        meta: {
          titleKey: 'staff.pages.draftReview.title',
          icon: 'fact_check',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.chat.manage',
        },
      },
      {
        path: 'health',
        name: 'knowledge-management-health',
        component: () => import('src/views/pages/authenticated/ContentHealthPage.vue'),
        meta: {
          titleKey: 'contentHealth.title',
          icon: 'monitor_heart',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.chat.manage',
        },
      },
    ],
  },

  // User Management - requires staff.users.view
  {
    path: '/users',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'users',
        component: () => import('src/views/pages/authenticated/UsersPage.vue'),
        meta: {
          titleKey: 'staff.pages.users.title',
          icon: 'mdi-account-group',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.users.view',
        },
      },
    ],
  },

  // Workspace Management - requires staff.workspaces.view
  {
    path: '/workspaces',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'workspaces',
        component: () => import('src/views/pages/authenticated/WorkspacesPage.vue'),
        meta: {
          titleKey: 'staff.pages.workspaces.title',
          icon: 'mdi-office-building',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.workspaces.view',
        },
      },
    ],
  },

  // Advertisement Management - requires staff.ads.view
  {
    path: '/ads',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'ads',
        component: () => import('src/views/pages/authenticated/AdsPage.vue'),
        meta: {
          titleKey: 'staff.pages.ads.title',
          icon: 'mdi-advertisements',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.ads.view',
        },
      },
    ],
  },

  // Advertiser Accounts - requires staff.advertisers.view. Distinct from
  // /ads above, which manages Advertisement (placement) rows, not Advertiser
  // accounts.
  {
    path: '/advertisers',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'advertisers',
        component: () => import('src/views/pages/authenticated/AdvertisersPage.vue'),
        meta: {
          titleKey: 'staff.pages.advertisers.title',
          icon: 'mdi-storefront',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.advertisers.view',
        },
      },
    ],
  },

  // Marketing Email Template Preview - requires staff.marketing.templates.view.
  // Read-only: renders worker email templates with fixture data in the SI.
  {
    path: '/marketing-templates',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'marketing-templates',
        component: () => import('src/views/pages/authenticated/MarketingTemplatesPage.vue'),
        meta: {
          titleKey: 'staff.pages.marketingTemplates.title',
          icon: 'mdi-email-newsletter',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.marketing.templates.view',
        },
      },
    ],
  },

  // Marketplace Lead Review - requires staff.marketplace-leads.view
  {
    path: '/marketplace-leads',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'marketplace-leads',
        component: () => import('src/views/pages/authenticated/MarketplaceLeadsPage.vue'),
        meta: {
          titleKey: 'staff.pages.marketplaceLeads.title',
          icon: 'mdi-handshake',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.marketplace-leads.view',
        },
      },
    ],
  },

  // Acquisition (UTM-tracked inbound traffic) - requires staff.acquisition.view.
  // ACQUISITION DOMAIN — distinct from /ads (monetization).
  // Boundary: wiki/current/architecture/features/acquisition-vs-monetization.md
  {
    path: '/acquisition',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'acquisition',
        component: () => import('src/views/pages/authenticated/AcquisitionPage.vue'),
        meta: {
          titleKey: 'staff.pages.acquisition.title',
          icon: 'mdi-target',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.acquisition.view',
        },
      },
    ],
  },

  // Audit Logs - requires staff.audit.view
  {
    path: '/audit',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'audit',
        component: () => import('src/views/pages/authenticated/AuditPage.vue'),
        meta: {
          titleKey: 'staff.pages.audit.title',
          icon: 'mdi-history',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.audit.view',
        },
      },
    ],
  },

  // Activity - unified cross-domain audit feed (staff.audit.view)
  {
    path: '/activity',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'activity',
        component: () => import('src/views/pages/authenticated/ActivityPage.vue'),
        meta: {
          titleKey: 'staff.pages.activity.title',
          icon: 'mdi-timeline-text',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.audit.view',
        },
      },
    ],
  },

  // Sent emails - transactional email ledger + resend (staff-only)
  {
    path: '/sent-emails',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'sent-emails',
        component: () => import('src/views/pages/authenticated/SentEmailsPage.vue'),
        meta: {
          titleKey: 'staff.pages.sentEmails.title',
          icon: 'mdi-email-fast',
          requiresAuth: true,
          requiresStaff: true,
        },
      },
    ],
  },

  // Client Logs - requires staff.client-logs.view
  {
    path: '/client-logs',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'client-logs',
        component: () => import('src/views/pages/authenticated/ClientLogsPage.vue'),
        meta: {
          titleKey: 'staff.pages.clientLogs.title',
          icon: 'mdi-file-document-outline',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.client-logs.view',
        },
      },
    ],
  },

  // Client Log Policies - requires staff.client-log-policies.view.
  // Cross-user oversight of remote debug-logging policies (per-user create
  // still lives in the user drawer); surfaces the otherwise drawer-only list
  // plus the update/disable actions.
  {
    path: '/client-log-policies',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'client-log-policies',
        component: () => import('src/views/pages/authenticated/ClientLogPoliciesPage.vue'),
        meta: {
          titleKey: 'staff.pages.clientLogPolicies.title',
          icon: 'mdi-bug-check',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.client-log-policies.view',
        },
      },
    ],
  },

  // Deletion Requests - requires staff.deletion-requests.view
  {
    path: '/deletion-requests',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'deletion-requests',
        component: () => import('src/views/pages/authenticated/DeletionRequestsPage.vue'),
        meta: {
          titleKey: 'staff.pages.deletionRequests.title',
          icon: 'mdi-delete-alert',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.deletion-requests.view',
        },
      },
      {
        path: ':uuid',
        name: 'deletion-request-detail',
        component: () => import('src/views/pages/authenticated/DeletionRequestDetailPage.vue'),
        meta: {
          titleKey: 'staff.pages.deletionRequests.detail',
          icon: 'mdi-delete-alert',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.deletion-requests.view',
        },
      },
    ],
  },

  // Messaging Oversight - requires staff.conversations.view
  // Read-only, audited view of any workspace's conversations + message
  // threads, with per-conversation NDJSON export.
  {
    path: '/conversations',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'conversations',
        component: () => import('src/views/pages/authenticated/ConversationsPage.vue'),
        meta: {
          titleKey: 'staff.pages.conversations.title',
          icon: 'mdi-message-text-outline',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.conversations.view',
        },
      },
      {
        path: ':uuid',
        name: 'conversation-detail',
        component: () => import('src/views/pages/authenticated/ConversationDetailPage.vue'),
        meta: {
          titleKey: 'staff.pages.conversations.detail',
          icon: 'mdi-message-text-outline',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.conversations.view',
        },
      },
    ],
  },

  // Message Moderation Queue (G12) - requires staff.conversations.moderate
  // Triage of user-reported messages: dismiss a report or redact (hide) the
  // offending message.
  {
    path: '/message-reports',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'message-reports',
        component: () => import('src/views/pages/authenticated/MessageReportsPage.vue'),
        meta: {
          titleKey: 'staff.pages.messageReports.title',
          icon: 'mdi-flag-outline',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.conversations.moderate',
        },
      },
    ],
  },

  // Content Moderation - requires staff.moderation.view
  {
    path: '/moderation',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'moderation',
        component: () => import('src/views/pages/authenticated/ModerationPage.vue'),
        meta: {
          titleKey: 'staff.pages.moderation.title',
          icon: 'mdi-shield-check',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.moderation.view',
        },
      },
    ],
  },

  // Exports oversight - staff-general list; the API gates each bundle
  // download per-type (owner or matching staff.export.{type} slug).
  {
    path: '/exports',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'exports',
        component: () => import('src/views/pages/authenticated/ExportsPage.vue'),
        meta: {
          titleKey: 'exports.title',
          icon: 'mdi-download',
          requiresAuth: true,
          requiresStaff: true,
        },
      },
    ],
  },

  // Legal Documents - staff-only (no specific slug; LegalDocumentPolicy gates on isStaff)
  {
    path: '/legal-documents',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'legal-documents',
        component: () => import('src/views/pages/authenticated/LegalDocumentsPage.vue'),
        meta: {
          titleKey: 'legalDocuments.title',
          icon: 'gavel',
          requiresAuth: true,
          requiresStaff: true,
        },
      },
    ],
  },

  // Feature Flags - gated on staff.settings.view: the API authorizes these
  // endpoints via SystemSettingsPolicy (it piggybacks on SystemSetting; there is
  // no dedicated FeatureFlagPolicy), so the route guard matches the enforced slug.
  {
    path: '/feature-flags',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'feature-flags',
        component: () => import('src/views/pages/authenticated/FeatureFlagsPage.vue'),
        meta: {
          titleKey: 'staff.pages.featureFlags.title',
          icon: 'mdi-flag',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.settings.view',
        },
      },
      {
        path: 'metrics',
        name: 'feature-flags-metrics',
        component: () => import('src/views/pages/authenticated/FeatureFlagsMetricsPage.vue'),
        meta: {
          titleKey: 'staff.pages.featureFlagsMetrics.title',
          icon: 'mdi-chart-line',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.settings.view',
        },
      },
      {
        path: 'ab-tests',
        name: 'feature-flags-ab-tests',
        component: () => import('src/views/pages/authenticated/FeatureFlagsABTestsPage.vue'),
        meta: {
          titleKey: 'staff.pages.featureFlagsABTests.title',
          icon: 'mdi-flask',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.settings.view',
        },
      },
    ],
  },

  // System Settings - requires staff.system.settings
  {
    path: '/settings',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'settings',
        component: () => import('src/views/pages/authenticated/SettingsPage.vue'),
        meta: {
          titleKey: 'staff.pages.settings.title',
          icon: 'mdi-cog',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.system.settings',
        },
      },
    ],
  },

  // Maintenance Mode - requires staff.maintenance.view
  {
    path: '/maintenance',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'maintenance',
        component: () => import('src/views/pages/authenticated/MaintenancePage.vue'),
        meta: {
          titleKey: 'staff.pages.maintenance.title',
          icon: 'mdi-wrench',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.maintenance.view',
        },
      },
    ],
  },

  // ML admin surfaces. Predictions + feedback require staff.ml.predictions.review.
  // The model registry is view-open to any staff (MLModelVersionPolicy::viewAny
  // is isStaff-only); staff.ml.models.manage gates only the promote/deprecate
  // actions, enforced on the buttons inside MLModelsPage.vue, not the route.
  {
    path: '/ml',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: 'predictions',
        name: 'ml-predictions',
        component: () => import('src/views/pages/authenticated/MLPredictionReviewPage.vue'),
        meta: {
          titleKey: 'staff.pages.mlPredictions.title',
          icon: 'mdi-robot',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.ml.predictions.review',
        },
      },
      {
        path: 'models',
        name: 'ml-models',
        component: () => import('src/views/pages/authenticated/MLModelsPage.vue'),
        meta: {
          titleKey: 'staff.pages.mlModels.title',
          icon: 'mdi-cube-outline',
          requiresAuth: true,
          requiresStaff: true,
        },
      },
      {
        path: 'feedback',
        name: 'ml-feedback',
        component: () => import('src/views/pages/authenticated/MLFeedbackPage.vue'),
        meta: {
          titleKey: 'staff.pages.mlFeedback.title',
          icon: 'mdi-message-reply-text',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.ml.predictions.review',
        },
      },
    ],
  },

  // Notification delivery metrics - piggy-backs on staff.settings.view
  // (the API authorizes via SystemSettingsPolicy::viewAny).
  {
    path: '/notification-metrics',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'notification-metrics',
        component: () => import('src/views/pages/authenticated/NotificationMetricsPage.vue'),
        meta: {
          titleKey: 'staff.pages.notificationMetrics.title',
          icon: 'mdi-bell-ring-outline',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.settings.view',
        },
      },
    ],
  },

  // ===========================================
  // TENANT DATA OVERSIGHT (read-only, audited, slug-gated)
  // Staff visibility into the business objects users create in the UI.
  // Plan: wiki/future/todo/20260708_080220_si_business_object_oversight.md
  // ===========================================
  {
    path: '/properties',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'properties',
        component: () => import('src/views/pages/authenticated/PropertyOversightPage.vue'),
        meta: {
          titleKey: 'staff.pages.properties.title',
          icon: 'mdi-home-city',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.properties.view',
        },
      },
    ],
  },
  {
    path: '/projects',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'projects',
        component: () => import('src/views/pages/authenticated/ProjectOversightPage.vue'),
        meta: { titleKey: 'staff.pages.projects.title', icon: 'mdi-folder-multiple', requiresAuth: true, requiresStaff: true, permission: 'staff.projects.view' },
      },
    ],
  },
  {
    path: '/tasks',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'tasks',
        component: () => import('src/views/pages/authenticated/TaskOversightPage.vue'),
        meta: { titleKey: 'staff.pages.tasks.title', icon: 'mdi-checkbox-marked-outline', requiresAuth: true, requiresStaff: true, permission: 'staff.tasks.view' },
      },
    ],
  },
  {
    path: '/teams',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'teams',
        component: () => import('src/views/pages/authenticated/TeamOversightPage.vue'),
        meta: { titleKey: 'staff.pages.teams.title', icon: 'mdi-account-group', requiresAuth: true, requiresStaff: true, permission: 'staff.teams.view' },
      },
    ],
  },
  {
    path: '/schedules',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'schedules',
        component: () => import('src/views/pages/authenticated/TaskScheduleOversightPage.vue'),
        meta: { titleKey: 'staff.pages.schedules.title', icon: 'mdi-calendar-clock', requiresAuth: true, requiresStaff: true, permission: 'staff.schedules.view' },
      },
    ],
  },
  {
    path: '/inventory',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'inventory',
        component: () => import('src/views/pages/authenticated/InventoryItemOversightPage.vue'),
        meta: { titleKey: 'staff.pages.inventory.title', icon: 'mdi-package-variant-closed', requiresAuth: true, requiresStaff: true, permission: 'staff.inventory.view' },
      },
    ],
  },
  {
    path: '/time-tracking',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'time-tracking',
        component: () => import('src/views/pages/authenticated/TimeEntryOversightPage.vue'),
        meta: { titleKey: 'staff.pages.timeTracking.title', icon: 'mdi-timer-outline', requiresAuth: true, requiresStaff: true, permission: 'staff.time-tracking.view' },
      },
    ],
  },
  {
    path: '/billing',
    component: () => import('src/views/layouts/AuthenticatedLayout.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
      {
        path: '',
        name: 'billing',
        component: () => import('src/views/pages/authenticated/BillingOversightPage.vue'),
        meta: {
          titleKey: 'staff.pages.billing.title',
          icon: 'mdi-receipt-text',
          requiresAuth: true,
          requiresStaff: true,
          permission: 'staff.invoices.view',
        },
      },
    ],
  },

  // 404 - Always leave this as last one
  {
    path: '/:catchAll(.*)*',
    component: () => import('src/views/pages/ErrorNotFound.vue'),
  },
];

export default routes;
